import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "./style.scss";
import PopUpOutline from "../PopUpOutline";
import ImageButton from "../ImageButton";
import { getRecordingInstances } from "../../shared/middleware/recordings";
import { is4KChannel } from "../../shared/utils/epg";
import { loadChannels } from "../../App/state/actions";
import { loadSportEventRecordingData } from "../../pages/PlayerPage/state/actions";
import { getFormattedRecordingDuration, isRecordingInProgress } from "../../shared/utils/recordingHelper";
import { getRegionalChannelNumber } from "../../shared/utils/epg";

const TeamsRecordingInstancesModal = (props) => {
  const {
    closeTeamsRecordingInstancesModal,
    appProvider,
    subscribedChannels,
    channelMapInfo,
    recordingResponse,
    initiatePlayback,
  } = props;
  const { t: translate } = useTranslation();
  const [teamInstances, setTeamInstances] = useState(null);
  const dispatch = useDispatch();
  const umId = recordingResponse?.metadata?.extendedMetadata?.dlum?.umId;
  const regionId = appProvider?.channelMapID;

  useEffect(() => {
    if (!channelMapInfo && appProvider) {
      dispatch(loadChannels(appProvider));
    }
  }, [appProvider, channelMapInfo, dispatch]);

  useEffect(() => {
    const getRecordingInstancesData = async () => {
      if (!subscribedChannels?.containers?.length || !channelMapInfo?.containers?.length) {
        return;
      }
      try {
        // Fetch recording instances using the umId
        const recordingInstancesData = await getRecordingInstances(appProvider, umId);
        if (recordingInstancesData?.containers?.length === 0) {
          return;
        }

        const teamInstancesData = {};
        for (const instance of recordingInstancesData.containers) {
          const channelId = instance.channel?.channelId;
          const { contentId, bookmarks } = instance.metadata || {};
          const externalId = instance.assets?.[0]?.externalId;

          if (!channelId) continue;

          teamInstancesData[channelId] = {
            contentId,
            externalId,
            bookmarks,
          };
        }

        setTeamInstances(teamInstancesData);
      } catch (error) {
        console.info(error);
      }
    };
    getRecordingInstancesData();
  }, [appProvider, channelMapInfo, subscribedChannels, umId]);

  const sortInstancesByChannelNumber = (instances) =>
    instances.sort((a, b) => {
      const aVal = a.regionalChannelNumber;
      const bVal = b.regionalChannelNumber;

      if (aVal && bVal) {
        return aVal - bVal;
      }
    });

  //create list of subscribed channels
  const { subscribedChannelsIds, subscribedInstances } = useMemo(() => {
    if (teamInstances) {
      const subscribedChannelsIds = [];
      let subscribedChannelsList = [];
      for (const channel of subscribedChannels.containers) {
        const channelId = channel.metadata.channelId;
        if (teamInstances[channelId]) {
          channel.regionalChannelNumber = getRegionalChannelNumber(channel, regionId) || channelId;
          subscribedChannelsIds.push(channelId);
          subscribedChannelsList.push(channel);
        }
      }
      if (subscribedChannelsList.length > 0) {
        subscribedChannelsList = sortInstancesByChannelNumber(subscribedChannelsList);
      }
      return { subscribedChannelsIds, subscribedInstances: subscribedChannelsList };
    }
    return {};
  }, [regionId, subscribedChannels, teamInstances]);

  const { isInProgress, durationText, stopDeltaTime } = useMemo(() => {
    if (!recordingResponse?.metadata) return { isInProgress: false, durationText: null, stopDeltaTime: null };

    const { recordingStartTime, startDeltaTime, stopDeltaTime } = recordingResponse.metadata;
    const isInProgress = isRecordingInProgress(recordingStartTime, startDeltaTime, stopDeltaTime);
    const recordingDuration = stopDeltaTime - startDeltaTime; // duration in milliseconds
    const durationText = !isInProgress ? getFormattedRecordingDuration(recordingDuration) : "";

    return { isInProgress, durationText, stopDeltaTime };
  }, [recordingResponse]);

  //create list of unsubscribed channels. Also filter out channels which are not available for user
  const unsubscribedInstances = useMemo(() => {
    if (teamInstances) {
      let unsubscribedChannelsList = [];
      for (const channel of channelMapInfo.containers) {
        const channelId = channel.metadata.channelId;
        if (teamInstances[channelId] && !subscribedChannelsIds.includes(channelId)) {
          channel.regionalChannelNumber = getRegionalChannelNumber(channel, regionId) || channelId;
          unsubscribedChannelsList.push(channel);
        }
      }
      if (unsubscribedChannelsList.length > 0) {
        unsubscribedChannelsList = sortInstancesByChannelNumber(unsubscribedChannelsList);
      }
      return unsubscribedChannelsList;
    }
    return null;
  }, [channelMapInfo, regionId, subscribedChannelsIds, teamInstances]);

  const onModalClose = useCallback(() => {
    closeTeamsRecordingInstancesModal();
  }, [closeTeamsRecordingInstancesModal]);

  const getRecordingInstance = (channelData, isSubscribed) => {
    const is4K = is4KChannel(channelData);
    const channelLogoUrl = channelData.assets?.[0]?.logoSmall || "";
    const badge4KUrl = process.env.PUBLIC_URL + "/images/4K_icon.svg";
    const playbackIconUrl = process.env.PUBLIC_URL + "/images/playback-play.svg";
    const inProgressIconUrl = process.env.PUBLIC_URL + "/images/Recording_Now_EN.svg";
    const keyIcon = process.env.PUBLIC_URL + "/images/key-icon.svg";
    const { channelId } = channelData.metadata;
    const regionalChannelNumber = channelData.regionalChannelNumber;
    const channelName = channelData.metadata.channelName;

    const getPlayResumeText = () => {
      if (!isSubscribed) {
        return translate("subscription_options");
      }
      let playText = "play";
      const instance = teamInstances?.[channelId];
      if (instance) {
        const { bookmarks } = instance;
        if (bookmarks?.length > 0 && bookmarks[0].isComplete === false) {
          playText = "resume";
        }
      }

      return translate(playText);
    };

    const onRecordingInstanceClick = () => {
      if (isSubscribed) {
        const instance = teamInstances?.[channelId];
        if (!instance) return;

        const { contentId, externalId } = instance;
        const sportEventRecordingData = {
          isSportRecording: true,
          recordingSeriesId: recordingResponse?.metadata?.recordingSeriesId,
          isTeamRecording: true,
        };
        dispatch(loadSportEventRecordingData({ [contentId]: sportEventRecordingData }));
        initiatePlayback(contentId, externalId, stopDeltaTime);
      }
    };

    return (
      <div className="recording-instances-item" key={channelData.id}>
        <div className="channel-icon">
          <img src={channelLogoUrl} />
        </div>
        <div className="program-details">
          <div className="play-resume">
            <div>{getPlayResumeText()}</div>
            {is4K && (
              <div className="icon-4k">
                <img src={badge4KUrl} />
              </div>
            )}
          </div>
          <div className="channel-name">{regionalChannelNumber + "-" + channelName}</div>
          <div className="duration">{isInProgress ? <img src={inProgressIconUrl} /> : durationText}</div>
        </div>
        <div className="recording-action" onClick={onRecordingInstanceClick}>
          {isSubscribed ? <img src={playbackIconUrl} /> : <img src={keyIcon} />}
        </div>
      </div>
    );
  };

  const modalContent = teamInstances ? (
    <>
      <div className="recording-instances-modal-backdrop" />
      <div className="recording-instances-modal-container">
        <div className="recording-instances-modal-popup-container">
          <PopUpOutline className="recording-instances-modal-popup">
            <div className="recording-instance-modal-header">
              <div className="modal-header-text"> {translate("recordings_label")}</div>
              <div className="modal-close">
                <ImageButton
                  src={process.env.PUBLIC_URL + "/images/playback-close.svg"}
                  alt={translate("close")}
                  onClickHandler={onModalClose}
                />
              </div>
            </div>
            {subscribedInstances?.length > 0 || unsubscribedInstances?.length > 0 ? (
              <div className="recording-instance-modal-content">
                {subscribedInstances.length > 0 && (
                  <div className="subscribed-instances-section">
                    <div className="section-header">{translate("select_channel")}</div>
                    <div className="subscribed-instances-list">
                      {subscribedInstances.map((channelData) => getRecordingInstance(channelData, true))}
                    </div>
                  </div>
                )}
                {unsubscribedInstances.length > 0 && (
                  <div className="unsubscribed-instances-section">
                    <div className="section-header">
                      {subscribedInstances.length > 0
                        ? translate("additional_channels")
                        : translate("subscription_add_channel")}
                    </div>
                    <div className="unsubscribed-instances-list">
                      {unsubscribedInstances.map((channelData) => getRecordingInstance(channelData, false))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="recording-unavailable">{translate("recording_unavailable_region")}</div>
            )}
          </PopUpOutline>
        </div>
      </div>
    </>
  ) : null;

  return ReactDOM.createPortal(modalContent, document.body);
};

function mapStateToProps({ app }) {
  return {
    subscribedChannels: app.subscribedChannels,
    channelMapInfo: app.channelMapInfo,
  };
}

export default connect(mapStateToProps)(TeamsRecordingInstancesModal);
